import { useSearchParams } from 'react-router-dom'
import { ArrowDown, ArrowUp, ChevronsUpDown, Inbox } from 'lucide-react'
import { buildTableSortSearchParams } from './helpers/buildTableSortSearchParams'
import { getColumnSortKey } from './helpers/getColumnSortKey'
import { readTableSortFromParams } from './helpers/readTableSortFromParams'
import { sortTableRows } from './helpers/sortTableRows'
import './DataTable.css'
import type { DataTableProps } from './DataTable.types'

export type { ColumnDef } from './DataTable.types'

export function DataTable<T>({
  columns,
  data,
  rowKey,
  emptyMessage = 'No data found.',
  onRowClick,
  defaultSortHeader,
}: DataTableProps<T>) {
  const [searchParams, setSearchParams] = useSearchParams()
  const sort = readTableSortFromParams(searchParams, columns, defaultSortHeader)
  const sortColumn = columns[sort.columnIndex]
  const sortedRows = sortColumn
    ? sortTableRows(data, sortColumn.sortValue, sort.direction)
    : data

  function handleSortClick(columnIndex: number) {
    const isSameColumn = columnIndex === sort.columnIndex
    const direction = isSameColumn && sort.direction === 'asc' ? 'desc' : 'asc'
    setSearchParams(
      buildTableSortSearchParams(
        searchParams,
        getColumnSortKey(columns[columnIndex].header),
        direction
      ),
      { replace: true }
    )
  }

  if (data.length === 0) {
    return (
      <div className="data-table-empty">
        <Inbox
          className="data-table-empty__icon"
          size={28}
          strokeWidth={1.5}
          aria-hidden="true"
        />
        <p className="data-table-empty__message">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className="table-wrapper">
      <div className="table-responsive">
        <table className="table align-middle mb-0 sticky-table">
          <thead>
            <tr>
              {columns.map((col, columnIndex) => {
                const isSorted = columnIndex === sort.columnIndex
                const SortIcon = !isSorted
                  ? ChevronsUpDown
                  : sort.direction === 'asc'
                    ? ArrowUp
                    : ArrowDown
                return (
                  <th
                    key={col.header}
                    scope="col"
                    aria-sort={
                      isSorted
                        ? sort.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                    className={
                      [col.className, col.headerClassName]
                        .filter(Boolean)
                        .join(' ') || undefined
                    }
                  >
                    <button
                      type="button"
                      className={`data-table__sort-button${isSorted ? ' data-table__sort-button--active' : ''}`}
                      onClick={() => handleSortClick(columnIndex)}
                    >
                      {col.header}
                      <SortIcon
                        className="data-table__sort-icon"
                        size={14}
                        strokeWidth={2}
                        aria-hidden="true"
                      />
                    </button>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr
                key={rowKey(row)}
                className={onRowClick ? 'clickable-row' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        if (event.key === 'Enter') {
                          onRowClick(row)
                        }
                      }
                    : undefined
                }
              >
                {columns.map((col) => (
                  <td
                    key={col.header}
                    className={
                      [col.className, col.cellClassName]
                        .filter(Boolean)
                        .join(' ') || undefined
                    }
                  >
                    {col.accessor(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="table-footer">
        <span>Total: {data.length}</span>
      </div>
    </div>
  )
}
